import {useEffect, useState} from 'react';
import {LogIn, LogOut, Pencil, User, X} from 'lucide-react';
import api from '../api/axios';
import {useAuth} from '../features/auth/AuthContext';

declare const google: any;

const UserLogin = () => {
    const {user, login, updateUser, logout} = useAuth();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handleGoogleLogin = () => {
        if (typeof google === 'undefined') return;

        const tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
            callback: async (tokenResponse: any) => {
                if (tokenResponse && tokenResponse.access_token) {
                    try {
                        const response = await api.post('/api/v1/auth/google', {
                            accessToken: tokenResponse.access_token
                        });

                        login(response.data);
                    } catch (error) {
                        console.error('Login failed:', error);
                    }
                }
            },
        });

        tokenClient.requestAccessToken();
    };

    const handleLogout = async () => {
        try {
            await api.post('/api/v1/auth/logout');
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            logout();
        }
    };

    const openEditModal = () => {
        setNameInput(user?.name || '');
        setErrorMessage(null);
        setIsEditOpen(true);
    };

    const handleUpdateName = async (event: React.FormEvent) => {
        event.preventDefault();

        const trimmedName = nameInput.trim();
        if (!trimmedName) {
            setErrorMessage('이름을 입력해 주세요.');
            return;
        }

        if (trimmedName.length > 30) {
            setErrorMessage('이름은 30자 이하로 입력해 주세요.');
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);

        try {
            const response = await api.patch('/api/v1/users/me', {
                name: trimmedName
            });

            updateUser(response.data);
            setIsEditOpen(false);
        } catch (error: any) {
            console.error('Name update failed:', error);
            setErrorMessage(error.response?.data?.message || '이름을 저장하지 못했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            {user ? (
                <div className="flex items-center gap-3 group relative cursor-pointer">
                    <div className="text-right hidden sm:block">
                        <div className="text-xs text-gray-500 dark:text-gray-400">Welcome</div>
                        <div className="text-sm font-bold text-gray-800 dark:text-gray-100 max-w-[100px] truncate">
                            {user.name || 'User'}님
                        </div>
                    </div>

                    {user.picture ? (
                        <img
                            src={user.picture}
                            alt="Profile"
                            className="w-9 h-9 rounded-full border border-gray-200 dark:border-gray-700"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <div
                            className="w-9 h-9 bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-300 rounded-full flex items-center justify-center font-bold">
                            <User className="w-5 h-5"/>
                        </div>
                    )}

                    <div
                        className="absolute top-full right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-2 hidden group-hover:block transition-all z-50 before:absolute before:w-full before:h-4 before:-top-4 before:left-0">
                        <div className="px-4 py-2 border-b border-gray-50 dark:border-gray-700 mb-1">
                            <p className="text-xs text-gray-400 dark:text-gray-500">Signed in as</p>
                            <p className="text-sm font-semibold truncate dark:text-gray-200">{user.email}</p>
                        </div>
                        <button
                            onClick={openEditModal}
                            className="w-full text-left px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                            <Pencil className="w-4 h-4"/> 내 정보 수정
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                        >
                            <LogOut className="w-4 h-4"/> 로그아웃
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={handleGoogleLogin}
                    className="flex items-center gap-2 bg-sky-100 hover:bg-sky-200 dark:bg-sky-900/30 dark:hover:bg-sky-900/50 text-sky-600 dark:text-sky-400 px-5 py-2 rounded-full text-sm font-bold transition-all shadow-sm hover:shadow-md transform active:scale-95"
                >
                    <LogIn className="w-4 h-4"/>
                    <span className="hidden sm:inline">Google 로그인</span>
                    <span className="sm:hidden">로그인</span>
                </button>
            )}

            {isEditOpen && user && (
                <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
                    <form
                        onSubmit={handleUpdateName}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 relative"
                    >
                        <button
                            type="button"
                            onClick={() => setIsEditOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5"/>
                        </button>

                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">내 정보 수정</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">표시 이름만 변경할 수 있습니다.</p>

                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" htmlFor="profile-name">
                            이름
                        </label>
                        <input
                            id="profile-name"
                            type="text"
                            value={nameInput}
                            onChange={(event) => setNameInput(event.target.value)}
                            maxLength={30}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900"
                            autoFocus
                        />

                        {errorMessage && (
                            <p className="text-sm text-red-500 mt-3">{errorMessage}</p>
                        )}

                        <div className="flex gap-2 mt-6">
                            <button
                                type="button"
                                onClick={() => setIsEditOpen(false)}
                                className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-bold transition-colors"
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 dark:disabled:bg-indigo-900/50 text-white font-bold transition-colors"
                            >
                                {isSaving ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
};

export default UserLogin;
